import React, { useEffect, useState } from 'react';
import { Wrap, Paragraph, Collapsible, Button } from '@leafwell/components';
import { JobsByTeamResult } from 'lib/queries/jobs';
import { useWordPressContext } from 'contexts/wordpress-provider';
import HeadingBody from './heading-body';
import WysiwygContent from './wysiwyg-content';

export type JobsListProps = {
  teams?: JobsByTeamResult['teams'];
};

const JobsList: React.FC<JobsListProps> = ({ teams }) => {
  const { translations } = useWordPressContext();
  const [hash, setHash] = useState(
    typeof window !== 'undefined' ? window.location.hash : '',
  );

  useEffect(() => {
    const handler = () => {
      setHash(prev => {
        const newHash = window.location.hash;
        if (prev !== newHash) {
          return newHash;
        }
        return prev;
      });
    };
    window.addEventListener('hashchange', () => handler());

    () => {
      window.removeEventListener('hashchange', () => handler());
    };
  }, []);

  return (
    <>
      <section className="bg-gray-100 lg:py-16 mt-10">
        <Wrap tagName="div">
          {teams ? (
            <HeadingBody
              title={translations['Open Positions']}
              level="6"
              className="font-medium"
              tagName="h4"
            />
          ) : (
            <HeadingBody
              title={translations['No Positions Found']}
              level="6"
              className="font-medium text-center"
              tagName="h4"
            />
          )}
          {teams &&
            Object.keys(teams)
              .sort()
              .map((team, index) => {
                return (
                  team &&
                  teams[team]?.length > 0 && (
                    <article
                      key={`team.${index}`}
                      className="grid grid-cols-1 md:grid-cols-4"
                    >
                      <Paragraph className="md:text-sm">
                        {team !== 'null' ? team : 'Others'}
                      </Paragraph>
                      <ul className="pt-6 md:pt-0 md:pl-24 col-span-3">
                        {teams[team]
                          .sort((a, b) => a.title.localeCompare(b.title))
                          .map(
                            (
                              {
                                title,
                                description,
                                requirements,
                                jobLocation,
                                applyUrl,
                                sectionId,
                                jobId,
                              },
                              index,
                            ) => {
                              const isFirst = index === 0;
                              const isHash =
                                sectionId === hash?.replace('#', '');

                              const content = `
                        ${description ? description : ''} 
                        ${requirements ? requirements : ''} 
                        `;

                              return (
                                <span key={jobId} id={sectionId}>
                                  <Collapsible
                                    title={title}
                                    subtitle={jobLocation}
                                    variant="border"
                                    className={isFirst ? '' : 'mt-4'}
                                    openCollapsible={isHash}
                                  >
                                    <WysiwygContent
                                      content={content}
                                      className="mt-4"
                                    />
                                    <Button
                                      variant="primary"
                                      label={translations['Apply now']}
                                      rounded={true}
                                      className={[
                                        'mb-4 !text-black',
                                        content?.length > 0 ? 'mt-8' : '',
                                      ].join(' ')}
                                      href={applyUrl}
                                      target="_blank"
                                    />
                                  </Collapsible>
                                </span>
                              );
                            },
                          )}
                      </ul>
                    </article>
                  )
                );
              })}
        </Wrap>
        <Wrap className="max-w-160 justify-items-center text-center lg:!pb-0 !gap-y-4">
          <Paragraph className="font-bold	" tagName="h4">
            {translations['Not Finding What You’re Looking For?']}
          </Paragraph>
          <Paragraph>
            {
              translations[
                'Share your information and we will contact you if new opportunities fitting your qualifications become available'
              ]
            }
          </Paragraph>
          <Button
            variant="primary"
            label={translations['Share your information']}
            rounded={true}
            href={`${process.env.NEXT_PUBLIC_PAYLOCITY_SHARE_API_URL}/${process.env.NEXT_PUBLIC_PAYLOCITY_SHARE_API_KEY}`}
            target="_blank"
          />
        </Wrap>
      </section>
      <Wrap tagName="footer">
        <Paragraph className="italic">
          {
            translations[
              'Leafwell is an equal opportunity workplace. We handle all applicants with the same respect that we give our employees.'
            ]
          }
        </Paragraph>
      </Wrap>
    </>
  );
};

export default JobsList;
